import { CardVariant } from '@src/components/product-card/product-card.types'

import BaseProduct from '../domains/Product/BaseProduct'
import Bundle, { PRODUCT_TYPES } from '../domains/Product/BundleProduct'
import ProductOptionValue, { ProductOptionName } from '../domains/ProductOption'
import ProductTag, { PRODUCT_TAG_TYPE } from '../domains/ProductTag'
import { BaseProductVariant, ProductVariantOption } from '../domains/ProductVariant'
import { PromoConfig } from '../domains/ProductVariant/PromoConfig'
import productHelper from '../helpers/productHelper'

// @i18n-ignore
const TODAY_ONLY_TAG = 'Today Only'
const tagsOnlyVisibleForStockProduct = [TODAY_ONLY_TAG]
const longOfferThreshold = 604800000 // 7 days in milliseconds

export const getValidPromo = (offers: PromoConfig[] | null | undefined) => {
  // Get UTC to time
  const currentDate = new Date()

  const todayTime = Date.parse(currentDate.toISOString())

  // if (isPreviewWebsite() && bfcmDay) {
  //   const newTodayTime = BFCM_DAY_CONF_MAP[bfcmDay];
  //   todayTime = Date.parse(newTodayTime);
  // }

  const validPromo = offers?.filter(offer => {
    const startTime = Date.parse(offer.startTime + 'Z')
    const endTime = Date.parse(offer.endTime + 'Z')
    return startTime <= todayTime && endTime >= todayTime
  })

  if (!validPromo || validPromo.length === 0) return null

  const promoTagMap: Record<string, boolean> = {}
  const tags: ProductTag[] = []
  let promoOfferMessage = ''

  for (const promo of validPromo) {
    promoOfferMessage += promo.promoOfferMessage ?? ''
    const isPromoALongOffer = isLongOffer(promo)
    promo.tags?.forEach(tag => {
      if (!promoTagMap[tag]) {
        promoTagMap[tag] = true
        tags.push(
          new ProductTag({
            value: tag,
            tagType: isPromoALongOffer ? PRODUCT_TAG_TYPE.PRIMARY : PRODUCT_TAG_TYPE.SECONDARY,
            options: [],
            showOnProductPage: true,
            showOnStockAvailability: tagsOnlyVisibleForStockProduct.includes(tag),
          }),
        )
      }
    })
  }

  return {
    ...validPromo[0],
    promoOfferMessage,
    tags,
  }
}

const getPromoData = (variant: BaseProductVariant | undefined) => {
  if (!variant) return null

  const promo = getValidPromo(variant.promoConfig)
  if (promo) return promo

  return null
}

export const getAdditionalProductTags = (
  product: BaseProduct | Bundle | undefined,
  selectedOptions: ProductVariantOption[] = [],
) => {
  if (!product || product.type === PRODUCT_TYPES.BUNDLE) {
    return []
  }
  const variants = productHelper.getVariantsForOptions(product, selectedOptions)
  const offerConfig = getPromoData(variants[0])
  return offerConfig ? offerConfig.tags : []
}

export const getOverriddenOfferMessage = (
  defaultOfferMessage: string | undefined,
  product?: BaseProduct | Bundle,
  selectedOptions: ProductVariantOption[] = [],
): string | undefined => {
  if (!product || product.type === PRODUCT_TYPES.BUNDLE) {
    return defaultOfferMessage
  }
  const variants = productHelper.getVariantsForOptions(product, selectedOptions)
  const offerConfig = getPromoData(variants[0])
  return offerConfig?.promoOfferMessage ?? defaultOfferMessage
}

export const getFestivalOfferPrice = ({
  product,
  selectedOptions,
}: {
  product: BaseProduct | Bundle
  selectedOptions?: ProductVariantOption[]
}) => {
  if (product.type === PRODUCT_TYPES.BUNDLE) return null

  const variants = productHelper.getVariantsForOptions(product, selectedOptions)
  const offerConfig = getPromoData(variants[0])

  return offerConfig?.promoPrice ?? null
}

export const getFestivalOfferPricePerUnit = ({
  product,
  selectedOptions,
}: {
  product: BaseProduct | Bundle
  selectedOptions?: ProductVariantOption[]
}) => {
  if (product.type === PRODUCT_TYPES.BUNDLE) return null

  const variants = productHelper.getVariantsForOptions(product, selectedOptions)
  const offerConfig = getPromoData(variants[0])

  return offerConfig?.promoPricePerUnit ?? null
}

export const getStoreCreditGrant = ({
  product,
  selectedOptions,
}: {
  product: BaseProduct | Bundle
  selectedOptions?: ProductVariantOption[]
}) => {
  if (product.type === PRODUCT_TYPES.BUNDLE) return 0

  const variants = productHelper.getVariantsForOptions(product, selectedOptions)
  const offerConfig = getPromoData(variants[0])

  return offerConfig?.storeCreditGrant ?? 0
}

export const getSubscriptionPrice = ({
  product,
  selectedOptions,
}: {
  product: BaseProduct | Bundle
  selectedOptions?: ProductVariantOption[]
}) => {
  if (product.type === PRODUCT_TYPES.BUNDLE) return null

  const variants = productHelper.getVariantsForOptions(product, selectedOptions)
  const offerConfig = getPromoData(variants[0])

  return offerConfig?.subscriptionPriceWithOffer ?? null
}

export const getSubscriptionOfferPricePerUnit = ({
  product,
  selectedOptions,
}: {
  product: BaseProduct | Bundle
  selectedOptions?: ProductVariantOption[]
}) => {
  if (product.type === PRODUCT_TYPES.BUNDLE) return null

  const variants = productHelper.getVariantsForOptions(product, selectedOptions)
  const offerConfig = getPromoData(variants[0])

  return offerConfig?.subscriptionPriceWithOfferPerUnit ?? null
}

export const getIsFinalSaleProduct = (
  product?: BaseProduct | Bundle,
  selectedOptions: ProductVariantOption[] = [],
): boolean => {
  if (!product || product.type === PRODUCT_TYPES.BUNDLE) {
    return false
  }

  const variants = productHelper.getVariantsForOptions(product, selectedOptions)
  const offerConfig = getPromoData(variants[0])
  return !!offerConfig?.isFinalSale
}

export const festivalOfferPriceForCard = (cardVariant: CardVariant | undefined) => {
  if (!cardVariant) return null

  const offerConfig = getValidPromo(cardVariant.promoConfig)
  return offerConfig?.promoPrice ?? null
}

export const subscriptionOfferPriceForCard = (cardVariant: CardVariant | undefined) => {
  if (!cardVariant) return null

  const offerConfig = getValidPromo(cardVariant.promoConfig)
  return offerConfig?.subscriptionPriceWithOffer ?? null
}

export const getAdditionalProductTagsForCard = (cardVariant: CardVariant | undefined) => {
  if (!cardVariant) return []

  const offerConfig = getValidPromo(cardVariant.promoConfig)
  return offerConfig?.tags ?? []
}

// This function is used to check if for selected color all the variants are on sale.
export const areAllColorVariantsOnSale = ({
  product,
  optionValue,
  optionName,
}: {
  product: BaseProduct | Bundle | undefined
  optionValue: ProductOptionValue
  optionName?: string
}): boolean => {
  if (!product || !optionValue.value || optionName !== ProductOptionName.Color) {
    return false
  }

  const colorOption: ProductVariantOption = { name: optionName, value: optionValue.value }
  const colorVariants = productHelper.getVariantsForOptions(product, [colorOption])

  if (colorVariants.length === 0) {
    return false
  }

  return colorVariants.every(variant => {
    const validPromoConfig = getValidPromo(variant.promoConfig)
    return !!validPromoConfig?.promoPrice
  })
}

export const isLongOffer = (promoConfig: PromoConfig) => {
  if (!promoConfig) return false
  if (!promoConfig.endTime) return true
  const startTime = Date.parse(promoConfig.startTime + 'Z') || 0
  const endTime = Date.parse(promoConfig.endTime + 'Z')

  return endTime - startTime > longOfferThreshold
}

/** Returns which price style to apply: festival takes precedence, then subscription (if selected), else default. */
export type PriceStyleKey = 'festival' | 'subscription' | 'default'

export const getPriceStyleKey = (
  hasFestivalOffer: boolean,
  hasSubscriptionOffer: boolean,
): PriceStyleKey => {
  return hasFestivalOffer ? 'festival' : hasSubscriptionOffer ? 'subscription' : 'default'
}

export const getOfferPricesForProductCard = (
  subscriptionEnabled: boolean,
  selectedCardVariant: CardVariant | undefined,
  productPromoConfig: PromoConfig[] | null | undefined
) => {
  if (subscriptionEnabled && selectedCardVariant) {
    const validPromo = getValidPromo(productPromoConfig)
    return {
      festivalPrice: validPromo?.promoPrice,
      subscriptionPrice: validPromo?.subscriptionPriceWithOffer,
    }
  }
  return {
    festivalPrice: festivalOfferPriceForCard(selectedCardVariant),
    subscriptionPrice: subscriptionOfferPriceForCard(selectedCardVariant),
  }
}